/**
 * Widget tag namespacing.
 *
 * Widgets are registered in a single global `customElements` registry,
 * so two packages that both `customElements.define("arch-thing", …)`
 * collide — last writer wins, silently. To make widget tags safe to
 * author, every package gets an implicit namespace derived from its
 * name and a bare `widget:` reference is auto-prefixed with it:
 *
 *   package `arch.c4`   →  prefix `arch-c4`
 *   `cascade widget: boundary`   →  `arch-c4-boundary`
 *
 * The rule is deliberately simple and non-breaking:
 *
 *   - A widget identifier that contains a hyphen is taken as a *full*
 *     tag and left verbatim (`arch-module`, `arch-backend-database`,
 *     and any cross-package reference like `arch-ui-card`). Every tag
 *     shipped before namespacing existed is hyphenated, so nothing
 *     migrates.
 *   - A bare, single-token identifier (no hyphen) is a short name and
 *     gets the declaring package's prefix prepended.
 *
 * So authors opt into namespacing by writing the short name; to reach
 * another package's widget they write its full hyphenated tag.
 *
 * This runs per package during package resolution, before the cascade
 * stamps a type's `widget` onto its instances — so the qualified tag
 * propagates to every subtype and instance, including those in other
 * packages that import the type.
 *
 * Inline-HTML widgets (`widget: "<html>"`) are string values, not
 * identifiers, and are never touched.
 */

import type {
  Body,
  SurfaceDecl,
  FieldDecl,
  InterfaceBody,
  InterfaceDecl,
  ModuleDecl,
  ParsedFile,
  TopLevelDecl,
  TypeDecl,
} from "./types.js";

/** Dotted package name → tag prefix (`arch.c4` → `arch-c4`). */
export function libraryPrefix(library: string): string {
  return library.replace(/\./g, "-");
}

/**
 * Rewrite every bare `widget:` identifier in a parsed file with the
 * given library's prefix. Returns the file unchanged when `library` is
 * undefined (anonymous package — nothing to namespace against).
 */
export function qualifyWidgetTags(
  file: ParsedFile,
  library: string | undefined
): ParsedFile {
  if (!library) return file;
  const prefix = libraryPrefix(library);

  let changed = false;
  const declarations = file.declarations.map((d) => {
    const next = qualifyDecl(d, prefix);
    if (next !== d) changed = true;
    return next;
  });
  return changed ? { ...file, declarations } : file;
}

function qualifyDecl(d: TopLevelDecl, prefix: string): TopLevelDecl {
  switch (d.kind) {
    case "type": {
      const body = qualifyBody(d.decl.body, prefix);
      return body === d.decl.body ? d : { ...d, decl: { ...d.decl, body } as TypeDecl };
    }
    case "module": {
      const body = qualifyBody(d.decl.body, prefix);
      return body === d.decl.body ? d : { ...d, decl: { ...d.decl, body } as ModuleDecl };
    }
    case "extension": {
      // §4.16: an extension body is qualified like a module body.
      const body = qualifyBody(d.decl.body, prefix);
      return body === d.decl.body ? d : { ...d, decl: { ...d.decl, body } };
    }
    default:
      return d;
  }
}

function qualifyBody(body: Body, prefix: string): Body {
  let changed = false;

  const fields = qualifyFields(body.fields, prefix);
  if (fields !== body.fields) changed = true;

  const modules = body.modules.map((m) => {
    const b = qualifyBody(m.body, prefix);
    if (b === m.body) return m;
    changed = true;
    return { ...m, body: b };
  });

  const surfaces = body.surfaces.map((f) => {
    const b = qualifyBody(f.body, prefix);
    if (b === f.body) return f;
    changed = true;
    return { ...f, body: b } as SurfaceDecl;
  });

  const interfaces = body.interfaces.map((i) => {
    const b = qualifyInterfaceBody(i.body, prefix);
    if (b === i.body) return i;
    changed = true;
    return { ...i, body: b } as InterfaceDecl;
  });

  if (!changed) return body;
  return { ...body, fields, modules, surfaces, interfaces };
}

function qualifyInterfaceBody(body: InterfaceBody, prefix: string): InterfaceBody {
  const fields = qualifyFields(body.fields, prefix);
  return fields === body.fields ? body : { ...body, fields };
}

function qualifyFields(
  fields: readonly FieldDecl[],
  prefix: string
): readonly FieldDecl[] {
  let changed = false;
  const out = fields.map((f) => {
    // Only the bare `widget` field carries the tag. `widget.<prop>`
    // fields are config, never tags.
    if (
      f.path.length === 1 &&
      f.path[0] === "widget" &&
      f.value?.kind === "identifier" &&
      !f.value.text.includes("-")
    ) {
      changed = true;
      return { ...f, value: { ...f.value, text: `${prefix}-${f.value.text}` } };
    }
    return f;
  });
  return changed ? out : fields;
}
